var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from "react";
import { Typography, Box, Chip, } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContainer, PageContent } from "../../../components/wraper";
import { PrimaryGrid } from "../../../components/grid/PrimaryGrid";
import { GridFilters } from "../../../components/grid/GridFilters";
import PageHeader from "@ui/components/header/PageHeader";
import { StatusIndicator } from "@ui/components/status";
import { SignalMeter } from "@ui/components/icons/SignalMeter";
import { SMenu } from "@ui/components/menu/SMenu";
import { useTranslation } from "react-i18next";
import { useUserProfileData, useUserProfileActions } from "./hooks";
import { colCenter } from "@ui/components/style/style";
import { SButton } from "@ui/components/buttons/SButton";
import { Edit, Trash } from "iconsax-react";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { deleteUser } from "@reducers/users/thunk/user-thunk";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
import { toast } from "@ui/utils/toaster";
import { EmbeddedAPProfile } from "./components/EmbeddedAPProfile";
import { UserProfileSkeleton } from "./components/UserProfileSkeleton";
import { RemoveAPDialog } from "./components/RemoveAPDialog";
import { usersApi } from "@api/enpoints/users-api";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { GridCellStack } from "@ui/components/grid";
import { AccessPointCard } from "./components/AccessPointCard";
import { UserProfileCard } from "./components/UserProfileCard";
const UserProfilePage = () => {
    const { t } = useTranslation(["form", "validation", "common"]);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { userId: userIdFromParams } = useParams();
    const { impersonation, user: authUser } = useAppSelector((state) => state.auth);
    // Use impersonated user ID if impersonating and no userId in URL, otherwise use URL param
    const userId = userIdFromParams ||
        (impersonation.isImpersonating ? impersonation.impersonatedUserId : null) ||
        (authUser === null || authUser === void 0 ? void 0 : authUser.id);
    // State for embedded AP Profile
    const [selectedApForProfile, setSelectedApForProfile] = useState(null);
    // State for Remove AP Dialog
    const [removeApDialogOpen, setRemoveApDialogOpen] = useState(false);
    const [selectedApForRemoval, setSelectedApForRemoval] = useState(null);
    const [removingAp, setRemovingAp] = useState(false);
    // User profile menu state
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    // Use profile hooks
    const { user, accessPoints, loading, searchTerm, filters, handleFilterChange, handleSearchChange, } = useUserProfileData(userId);
    const { handleViewAccessPoint, handleEditAccessPoint, handleRowClick, handleBackToUsers, } = useUserProfileActions();
    // Handler to open Remove AP dialog
    const handleRemoveApClick = (ap) => {
        setSelectedApForRemoval(ap);
        setRemoveApDialogOpen(true);
    };
    // Handler to confirm AP removal
    const handleConfirmRemoveAp = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!selectedApForRemoval || !userId)
            return;
        setRemovingAp(true);
        try {
            const response = yield usersApi.removeAccessPointFromUser({
                userId: userId,
                accessPointUid: selectedApForRemoval.deviceId,
            });
            if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.isSuccess) {
                toast.success(t("common:toasts.accessPointRemovedSuccessfully"));
                setRemoveApDialogOpen(false);
                setSelectedApForRemoval(null);
                // Refresh the page data
                window.location.reload();
            }
            else {
                toast.error(((_b = response.data) === null || _b === void 0 ? void 0 : _b.message) ||
                    t("common:toasts.failedToRemoveAccessPoint"));
            }
        }
        catch (error) {
            console.error("Error removing Access Point:", error);
            toast.error(t("common:toasts.errorRemovingAccessPoint"));
        }
        finally {
            setRemovingAp(false);
        }
    });
    // Handler to close Remove AP dialog
    const handleCloseRemoveApDialog = () => {
        setRemoveApDialogOpen(false);
        setSelectedApForRemoval(null);
    };
    // Menu handlers
    const handleUserMenuClick = (event) => {
        setUserMenuAnchorEl(event.currentTarget);
    };
    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
    };
    const handleEditUser = () => {
        handleUserMenuClose();
        if (userId) {
            navigate(`/users/${userId}`);
        }
    };
    const handleDeleteUser = () => __awaiter(void 0, void 0, void 0, function* () {
        handleUserMenuClose();
        if (userId) {
            if (window.confirm(`Are you sure you want to delete this user?`)) {
                try {
                    yield dispatch(deleteUser(userId))
                        .unwrap()
                        .then(() => {
                        toast.success(t("common:toasts.userDeletedSuccessfully"));
                        navigate("/users");
                    })
                        .catch((error) => {
                        toast.error(error || t("common:toasts.failedToDeleteUser"));
                    });
                }
                catch (error) {
                    console.error("Error deleting user:", error);
                }
            }
        }
    });
    // User profile menu items (gated by permission)
    const userMenuItems = [
        ...(PermissionChecker.hasPermission(Permission.USERS_EDIT)
            ? [
                {
                    label: t("common:actions.edit"),
                    icon: React.createElement(Edit, { size: 16 }),
                    onClick: handleEditUser,
                },
            ]
            : []),
        ...(PermissionChecker.hasPermission(Permission.USERS_DELETE)
            ? [
                {
                    label: t("common:actions.delete"),
                    icon: React.createElement(Trash, { size: 16 }),
                    onClick: handleDeleteUser,
                    color: "error",
                },
            ]
            : []),
    ];
    const columns = [
        {
            field: "ap",
            headerName: t("common:grid.ap"),
            minWidth: 80,
            filterable: false,
            renderCell: () => (React.createElement(Box, { sx: { display: "flex", justifyContent: "center" } },
                React.createElement(SignalMeter, { width: 66, height: 57 }))),
        },
        {
            field: "nameId",
            headerName: t("common:grid.nameId"),
            flex: 2,
            minWidth: 200,
            filterable: true,
            filterType: "text",
            renderCell: (params) => (
            // <Box>
            //   <Typography variant="body2" sx={{ fontWeight: 500 }}>
            //     {params.row.name}
            //   </Typography>
            //   <Typography variant="caption" color="text.secondary">
            //     {params.row.deviceId}
            //   </Typography>
            // </Box>
            React.createElement(GridCellStack, { topText: params.row.name, bottomText: params.row.deviceId })),
        },
        {
            field: "devices",
            headerName: t("common:grid.devices"),
            flex: 0.8,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => (React.createElement(Chip, { label: `${params.row.devices} ${t("common:grid.devices")}`, size: "small", variant: "soft", color: "default" })),
        },
        {
            field: "status",
            headerName: t("common:grid.status"),
            flex: 1,
            minWidth: 120,
            filterable: true,
            filterType: "select",
            filterOptions: ["active", "inactive"],
            renderCell: (params) => (React.createElement(StatusIndicator, { variant: params.row.status })),
        },
        {
            field: "lastUpdate",
            headerName: t("common:grid.lastUpdate"),
            flex: 1.5,
            minWidth: 150,
            filterable: true,
            filterType: "text",
            renderCell: (params) => (React.createElement(GridCellStack, { topText: params.row.lastUpdate
                    ? formatUtcToLocalDate(params.row.lastUpdate)
                    : t("common:actions.never"), bottomText: params.row.lastUpdate
                    ? formatUtcToLocalTime(params.row.lastUpdate)
                    : "" })),
        },
        {
            field: "actions",
            headerName: t("common:grid.actions"),
            minWidth: 270,
            filterable: false,
            align: "right",
            renderCell: (params) => (React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                PermissionChecker.hasPermission(Permission.USERS_EDIT) && (React.createElement(SButton, { variant: "soft", color: "error", size: "small", onClick: (e) => {
                        e.stopPropagation();
                        handleRemoveApClick({
                            id: params.row.id,
                            name: params.row.name,
                            deviceId: params.row.deviceId,
                        });
                    } }, t("common:actions.remove"))),
                React.createElement(SButton, { variant: "soft", size: "small", color: "primary", onClick: (e) => {
                        e.stopPropagation();
                        setSelectedApForProfile(params.row.deviceId);
                    } }, t("common:actions.nodes")),
                PermissionChecker.hasPermission(Permission.ACCESSPOINTS_EDIT) && (React.createElement(SButton, { variant: "outlined", size: "small" }, t("common:actions.settings"))))),
        },
    ];
    if (loading) {
        return (React.createElement(PageContainer, null,
            React.createElement(PageHeader, { title: t("common:pages.userProfile"), showBackButton: true }),
            React.createElement(PageContent, { sx: { pb: { xs: 1, sm: 0 } } },
                React.createElement(UserProfileSkeleton, null))));
    }
    if (!user) {
        return (React.createElement(PageContainer, null,
            React.createElement(PageHeader, { title: t("common:pages.userNotFound"), showBackButton: true }),
            React.createElement(Typography, null, t("common:actions.userNotFoundOrLoading"))));
    }
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.userProfile"), showBackButton: true }),
        React.createElement(PageContent, { sx: { pb: { xs: 1, sm: 0 } } },
            React.createElement(Box, null,
                React.createElement(UserProfileCard, { user: user, onEdit: handleEditUser, onDelete: handleDeleteUser, onMenuClick: handleUserMenuClick })),
            selectedApForProfile ? (React.createElement(Box, null,
                React.createElement(Box, { sx: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    } },
                    React.createElement(Typography, { variant: "h6" }, t("common:pages.apProfile")),
                    React.createElement(SButton, { variant: "outlined", size: "small", onClick: () => setSelectedApForProfile(null) }, t("common:actions.backToAccessPoints"))),
                React.createElement(EmbeddedAPProfile, { apId: selectedApForProfile, onClose: () => setSelectedApForProfile(null) }))) : (React.createElement(React.Fragment, null,
                React.createElement(GridFilters, { title: `${accessPoints.length} Access Points`, columns: columns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange }),
                React.createElement(PrimaryGrid, { rows: accessPoints, columns: columns, loading: loading, paginationModel: { page: 0, pageSize: 3 }, heightVariant: "60%", checkboxSelection: false, autoHeight: true, renderCard: (row) => (React.createElement(AccessPointCard, { row: row, onClick: () => setSelectedApForProfile(row.deviceId), onRemove: (ap) => handleRemoveApClick(ap), onNodes: (deviceId) => setSelectedApForProfile(deviceId) })), onRowClick: (params, event) => {
                        // Don't navigate if clicking on action buttons or columns
                        const target = event.target;
                        if (target.closest('[data-field="actions"]') ||
                            target.closest("button") ||
                            target.closest('[role="button"]')) {
                            return;
                        }
                        // Show embedded AP Profile (same as Dashboard)
                        if (params.row.deviceId) {
                            setSelectedApForProfile(params.row.deviceId);
                        }
                    } }))),
            React.createElement(SMenu, { anchorEl: userMenuAnchorEl, open: Boolean(userMenuAnchorEl), handleClose: handleUserMenuClose, menuItems: userMenuItems }),
            React.createElement(RemoveAPDialog, { isOpen: removeApDialogOpen, onClose: handleCloseRemoveApDialog, onConfirm: handleConfirmRemoveAp, accessPoint: selectedApForRemoval, userName: user ? `${user.firstName} ${user.lastName}` : undefined, loading: removingAp }))));
};
export default UserProfilePage;
